/**
 * PromoCodeInput Component
 *
 * MVP 1.22 - Experience Pricing & Promotions Engine
 * Story: SCRUM-374 - Apply Promotional Code
 *
 * Promo code input with validation states and auto-apply support.
 *
 * Acceptance Criteria:
 * - AC-003.1: Input hidden behind "Have a promo code?" link
 * - AC-003.2: Valid code accepted within 300ms
 * - AC-003.3: Invalid/expired codes show clear error message
 * - AC-003.4: Usage limit reached shows specific message
 * - AC-003.5: Applied code shows green checkmark and discount
 * - AC-003.7: Private codes only work when entered
 * - AC-003.8: Public promotions auto-apply when eligible
 * - AC-003.10: Auto-applied shows "Promotion applied!" banner
 * - AC-003.11: "Use different code" to override auto-applied promotion
 */

import React, { useState, useCallback, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import { validatePromoCode } from '../../services/pricingApi';
import { formatCentsToDollars } from '../../utils/formHelpers';

/**
 * Map error reasons to user-friendly messages (AC-003.3, AC-003.4)
 */
const getErrorMessage = (reason) => {
  const messages = {
    INVALID_CODE: 'This promo code is not valid.',
    PROMOTION_NOT_ACTIVE: 'This promotion is no longer active.',
    NOT_YET_STARTED: 'This promotion hasn\'t started yet.',
    EXPIRED: 'This promo code has expired.',
    MAX_REDEMPTIONS_REACHED: 'This code has reached its maximum number of uses.',
    PARTY_TOO_SMALL: 'This code requires a larger party size.',
    BOOKING_AMOUNT_TOO_LOW: 'Your booking amount is below the minimum for this code.',
    TRIP_DATE_TOO_EARLY: 'This code is not valid for your selected trip date.',
    TRIP_DATE_TOO_LATE: 'This code is not valid for your selected trip date.'
  };
  return messages[reason] || 'Unable to apply this promo code.';
};

/**
 * Auto-Applied Promotion Banner (AC-003.10)
 */
const AutoAppliedBanner = ({ promotion, savingsAmount, onUseDifferentCode }) => (
  <div className="bg-green-50 border border-green-200 rounded-lg p-3">
    <div className="flex items-center justify-between">
      <div className="flex items-center gap-2">
        <svg className="w-5 h-5 text-green-500" fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
        </svg>
        <div>
          <p className="text-sm font-medium text-green-800">
            Promotion applied!
          </p>
          <p className="text-xs text-green-600">
            {promotion.name} - You&apos;re saving {formatCentsToDollars(savingsAmount)}
          </p>
        </div>
      </div>
      <button
        type="button"
        onClick={onUseDifferentCode}
        className="text-xs text-green-600 hover:text-green-800 underline"
      >
        Use different code
      </button>
    </div>
  </div>
);

AutoAppliedBanner.propTypes = {
  promotion: PropTypes.shape({
    name: PropTypes.string.isRequired,
    code: PropTypes.string
  }).isRequired,
  savingsAmount: PropTypes.number.isRequired,
  onUseDifferentCode: PropTypes.func.isRequired
};

/**
 * Applied Code Success State (AC-003.5)
 */
const AppliedCodeDisplay = ({ code, promotion, savingsAmount, onRemove }) => (
  <div className="flex items-center justify-between p-3 bg-green-50 border border-green-200 rounded-lg">
    <div className="flex items-center gap-2">
      <svg className="w-5 h-5 text-green-500" fill="currentColor" viewBox="0 0 20 20">
        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
      </svg>
      <div>
        <p className="text-sm font-medium text-green-800">
          {code.toUpperCase()} applied
        </p>
        <p className="text-xs text-green-600">
          {promotion?.discountDescription || `You're saving ${formatCentsToDollars(savingsAmount)}`}
        </p>
      </div>
    </div>
    <button
      type="button"
      onClick={onRemove}
      className="text-sm text-green-600 hover:text-green-800"
    >
      Remove
    </button>
  </div>
);

AppliedCodeDisplay.propTypes = {
  code: PropTypes.string.isRequired,
  promotion: PropTypes.shape({
    discountDescription: PropTypes.string
  }),
  savingsAmount: PropTypes.number.isRequired,
  onRemove: PropTypes.func.isRequired
};

/**
 * PromoCodeInput Component
 */
const PromoCodeInput = ({
  experienceId,
  bookingValueCents,
  tripDate,
  onPromoApplied,
  onPromoRemoved,
  autoAppliedPromotion,
  autoAppliedSavings,
  className = ''
}) => {
  const [expanded, setExpanded] = useState(false);
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [appliedPromo, setAppliedPromo] = useState(null);
  const [appliedSavings, setAppliedSavings] = useState(0);
  const [showAutoApplied, setShowAutoApplied] = useState(true);

  const inputRef = useRef(null);

  // Show auto-applied banner if there's an auto-applied promotion
  const hasAutoApplied = autoAppliedPromotion && showAutoApplied && !appliedPromo;

  // Focus input when expanded
  useEffect(() => {
    if (expanded && inputRef.current) {
      inputRef.current.focus();
    }
  }, [expanded]);

  /**
   * Validate and apply promo code (AC-003.2: within 300ms)
   */
  const handleApply = useCallback(async () => {
    if (!code.trim()) {return;}

    setLoading(true);
    setError(null);

    try {
      const response = await validatePromoCode(code.trim(), experienceId, {
        bookingValueCents,
        tripDate
      });

      if (response.success && response.data.valid) {
        const promo = response.data;
        setAppliedPromo(promo);

        // Calculate savings (approximation - actual savings come from pricing calculation)
        const savings = promo.discountType === 'percentage'
          ? Math.round((bookingValueCents || 0) * promo.discountValue / 100)
          : promo.discountValue;
        setAppliedSavings(savings);

        // Notify parent
        if (onPromoApplied) {
          onPromoApplied(code.trim(), promo, savings);
        }

        setExpanded(false);
        setShowAutoApplied(false);
      } else {
        setError(getErrorMessage(response.data?.error || response.data?.reason || 'INVALID_CODE'));
      }
    } catch (err) {
      setError(getErrorMessage('INVALID_CODE'));
    } finally {
      setLoading(false);
    }
  }, [code, experienceId, bookingValueCents, tripDate, onPromoApplied]);

  /**
   * Remove applied promo code
   */
  const handleRemove = useCallback(() => {
    setAppliedPromo(null);
    setAppliedSavings(0);
    setCode('');
    setError(null);

    if (onPromoRemoved) {
      onPromoRemoved();
    }
  }, [onPromoRemoved]);

  /**
   * Handle "Use different code" for auto-applied promotions (AC-003.11)
   */
  const handleUseDifferentCode = useCallback(() => {
    setShowAutoApplied(false);
    setExpanded(true);

    if (onPromoRemoved) {
      onPromoRemoved();
    }
  }, [onPromoRemoved]);

  /**
   * Handle Enter key to apply code
   */
  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleApply();
    }
  }, [handleApply]);

  // If there's an applied promo, show success state
  if (appliedPromo) {
    return (
      <div className={className}>
        <AppliedCodeDisplay
          code={appliedPromo.code}
          promotion={appliedPromo}
          savingsAmount={appliedSavings}
          onRemove={handleRemove}
        />
      </div>
    );
  }

  // If there's an auto-applied promotion, show banner (AC-003.10)
  if (hasAutoApplied) {
    return (
      <div className={className}>
        <AutoAppliedBanner
          promotion={autoAppliedPromotion}
          savingsAmount={autoAppliedSavings}
          onUseDifferentCode={handleUseDifferentCode}
        />
      </div>
    );
  }

  return (
    <div className={className}>
      {/* Collapsed state (AC-003.1) */}
      {!expanded && (
        <button
          type="button"
          onClick={() => setExpanded(true)}
          className="text-sm text-green-600 hover:text-green-700 font-medium"
        >
          Have a promo code?
        </button>
      )}

      {/* Expanded state with input */}
      {expanded && (
        <div className="space-y-2">
          <div className="flex gap-2">
            <input
              ref={inputRef}
              type="text"
              value={code}
              onChange={(e) => {
                setCode(e.target.value.toUpperCase());
                setError(null);
              }}
              onKeyDown={handleKeyDown}
              placeholder="Enter promo code"
              className={`
                flex-1 px-3 py-2 text-sm border rounded-lg
                focus:ring-2 focus:ring-green-500 focus:border-green-500
                ${error ? 'border-red-300 bg-red-50' : 'border-gray-300'}
              `}
              disabled={loading}
              aria-label="Promo code"
              aria-invalid={!!error}
              aria-describedby={error ? 'promo-error' : undefined}
            />
            <button
              type="button"
              onClick={handleApply}
              disabled={loading || !code.trim()}
              className={`
                px-4 py-2 text-sm font-medium rounded-lg
                ${loading || !code.trim()
                  ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                  : 'bg-green-600 text-white hover:bg-green-700'}
              `}
            >
              {loading ? 'Checking...' : 'Apply'}
            </button>
          </div>

          {/* Error message (AC-003.3, AC-003.4) */}
          {error && (
            <p id="promo-error" className="text-sm text-red-600 flex items-center gap-1">
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
              {error}
            </p>
          )}

          {/* Cancel link */}
          <button
            type="button"
            onClick={() => {
              setExpanded(false);
              setCode('');
              setError(null);
            }}
            className="text-xs text-gray-500 hover:text-gray-700"
          >
            Cancel
          </button>
        </div>
      )}
    </div>
  );
};

PromoCodeInput.propTypes = {
  experienceId: PropTypes.string.isRequired,
  bookingValueCents: PropTypes.number,
  tripDate: PropTypes.string,
  onPromoApplied: PropTypes.func,
  onPromoRemoved: PropTypes.func,
  autoAppliedPromotion: PropTypes.shape({
    name: PropTypes.string.isRequired,
    code: PropTypes.string
  }),
  autoAppliedSavings: PropTypes.number,
  className: PropTypes.string
};

export default PromoCodeInput;
